import { TextInput } from "components/inputs/text-input/TextInput";
import { TextArea } from "components/inputs/textarea/Textarea";
import { ThumbnailUploader } from "components/video-upload/components/thumbnail-uploader";
import styles from "./index.module.scss";
import { CategoriesAssignment } from "components/inputs/autocomplete/categories-assignment";
import {
    BroadcastStatus,
    BroadcastWebLink,
    Category,
    CategoryType
} from "@switcherstudio/switcher-api-client";
import { DisplaySelectedButton } from "components/buttons/DisplaySelectedButton";
import { PlayerSelectModal } from "components/modal/PlayerSelectModal";
import { WebLinkSelectModal } from "components/modal/WebLinkSelectModal";
import { ScheduleVideoVisibilityBar } from "components/inputs/schedule-video-visibility-bar";
import { useEffect, useState } from "react";
import { exists } from "helpers/booleans";
import { AspectRatio } from "views/page-content/cloud/upload/types";
import { useClaimCheck } from "hooks/useClaimCheck";
import { Toggle } from "components/inputs/toggle/Toggle";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { closeCurrentModal } from "store/modal/slice";
import { useDispatch } from "react-redux";
import { StreamQualitySelect } from "components/inputs/select/StreamQualitySelect";
import { StreamQualityOption } from "helpers/resolution";

export interface BroadcastDetailsFormProps {
    broadcastId?: string;
    title: string;
    setTitle: (title: string) => void;
    description: string;
    setDescription: (description: string) => void;
    streamQuality?: StreamQualityOption;
    setStreamQuality?: (streamQuality: StreamQualityOption) => void;
    setThumbnailFile: (thumbnailFile: File) => void;
    thumbnailImageURL: string;
    setThumbnailImageURL: (thumbnailImageURL: string) => void;
    selectedCategories?: Category[];
    setSelectedCategories?: (selectedCategories: Category[]) => void;
    onRemoveThumbnail?: () => void;
    showThumbnailUploader?: boolean;
    /** Enable controls for category selection */
    showCategories?: boolean;
    players?: string[];
    links?: BroadcastWebLink[];
    onLinkSave?: (links: BroadcastWebLink[]) => void;
    setAddToPlayers?: (players: string[]) => void;
    broadcastStatus?: BroadcastStatus;
    setBroadcastStatus?: (status: BroadcastStatus) => void;
    startsAt?: string;
    setStartsAt?: (date: string) => void;
    showInCatalog: boolean;
    setShowInCatalog: (showInCatalog: boolean) => void;
    liveBroadcast?: boolean;
    isUpload?: boolean;
    /** Provides the player's orientation for additional context.Currently indicates the recommend resolution for the thumbnail */
    orientation?: AspectRatio | undefined;
    disablePlayerSelect?: boolean;
    enableRecording?: boolean;
    filename?: string;
    filesize?: string;
    isPremiere?: boolean;
    isScheduledLivestream?: boolean;
    showCountdownOverThumbnail?: boolean;
    setShowCountdownOverThumbnail?: (
        showCountdownOverThumbnail: boolean
    ) => void;
}

export const BroadcastDetailsForm = ({
    broadcastId,
    title,
    setTitle,
    description,
    setDescription,
    streamQuality,
    setStreamQuality,
    setThumbnailFile,
    thumbnailImageURL,
    setThumbnailImageURL,
    selectedCategories,
    setSelectedCategories,
    onRemoveThumbnail,
    showThumbnailUploader = false,
    showCategories = false,
    players,
    links,
    onLinkSave,
    setAddToPlayers = () => {},
    broadcastStatus,
    setBroadcastStatus,
    liveBroadcast,
    startsAt,
    setStartsAt,
    isUpload = true,
    orientation,
    showInCatalog,
    setShowInCatalog,
    disablePlayerSelect = false,
    enableRecording = true,
    filename,
    filesize,
    isPremiere = false,
    isScheduledLivestream = false,
    showCountdownOverThumbnail,
    setShowCountdownOverThumbnail
}: BroadcastDetailsFormProps) => {
    const [playlistSelectModalIsOpen, setPlaylistSelectModalIsOpen] =
        useState<boolean>(false);
    const [linkSelectModalIsOpen, setLinkSelectModalIsOpen] =
        useState<boolean>(false);
    const hasCatalogClaim = useClaimCheck("catalog");
    const dispatch = useDispatch();

    const { t } = useTranslation();

    useEffect(() => {
        if (broadcastStatus === BroadcastStatus._5) {
            setShowInCatalog(false);
        } else if (
            broadcastStatus === BroadcastStatus._3 &&
            showInCatalog != false
        ) {
            setShowInCatalog(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [broadcastStatus, showInCatalog]);

    const emptyState = (
        <Trans
            i18nKey={t("players-select-modal:event-creation-empty")}
            components={{
                link1: (
                    <Link
                        to={"/catalog"}
                        title="Video Catalog"
                        className={styles["link"]}
                        onClick={() => dispatch(closeCurrentModal())}
                    />
                )
            }}
        />
    );

    return (
        <div className={"broadcast-details-form"}>
            {exists(players) && (
                <PlayerSelectModal
                    isOpen={playlistSelectModalIsOpen}
                    setIsOpen={setPlaylistSelectModalIsOpen}
                    handleSelect={setAddToPlayers}
                    previouslySelectedIds={players}
                    allowUnselect
                    allowNoSelection
                    emptyStateMessage={hasCatalogClaim && emptyState}
                    saveButtonText={
                        broadcastId !== undefined ? t("buttons:done") : null
                    }
                />
            )}
            <WebLinkSelectModal
                links={links}
                isOpen={linkSelectModalIsOpen}
                broadcastId={broadcastId}
                setIsOpen={setLinkSelectModalIsOpen}
                onSuccess={onLinkSave}
                isUpload={isUpload}
            />
            {filename && (
                <div className={`${styles["selected-file"]}`}>
                    <p className={`${styles["file-name"]}`}>
                        <strong>{t("video-upload:file")}:</strong>
                        {` ${filename}`}
                    </p>
                    <p className={`${styles["file-size"]} `}>{filesize}</p>
                </div>
            )}
            <div className={`${styles["broadcast-info"]}`}>
                <TextInput
                    id="name"
                    className="name"
                    label={t("video-upload:name")}
                    type="text"
                    value={title}
                    onChange={(event) => {
                        setTitle(event.target.value);
                    }}
                ></TextInput>
                <TextArea
                    id="description"
                    label={t("video-upload:description")}
                    value={description}
                    onChange={(event) => {
                        setDescription(event.target.value);
                    }}
                ></TextArea>
            </div>
            <div>
                <ScheduleVideoVisibilityBar
                    broadcastStatus={broadcastStatus}
                    setBroadcastStatus={setBroadcastStatus}
                    startsAt={startsAt}
                    setStartsAt={setStartsAt}
                    liveBroadcast={liveBroadcast}
                    isUpload={isUpload}
                    isPremiere={isPremiere}
                    isScheduledLivestream={isScheduledLivestream}
                />
                {hasCatalogClaim && enableRecording && (
                    <div className={`${styles["toggle-container"]}`}>
                        <Toggle
                            on={showInCatalog}
                            onToggle={() => setShowInCatalog(!showInCatalog)}
                            label={t("video-upload:show-in-catalog")}
                            reverseLayout
                            disabled={broadcastStatus === BroadcastStatus._5}
                        />
                    </div>
                )}
            </div>
            {isScheduledLivestream && (
                <div className={`${styles["quality-select-container"]}`}>
                    <h6>{t("video-upload:quality-select")}</h6>
                    <StreamQualitySelect
                        streamQuality={streamQuality}
                        setStreamQuality={setStreamQuality}
                    />
                </div>
            )}
            <div>
                {showThumbnailUploader && (
                    <div className={`${styles["thumbnail-container"]}`}>
                        <h6>{t("video-upload:thumbnail")}</h6>
                        <ThumbnailUploader
                            setThumbnailFile={setThumbnailFile}
                            thumbnailImageURL={thumbnailImageURL}
                            setThumbnailImageURL={setThumbnailImageURL}
                            onRemove={onRemoveThumbnail}
                            orientation={orientation}
                        />
                    </div>
                )}
                {enableRecording && (isPremiere || isScheduledLivestream) && (
                    <div className={`${styles["toggle-container"]}`}>
                        <Toggle
                            on={showCountdownOverThumbnail}
                            onToggle={() => {
                                setShowCountdownOverThumbnail(
                                    !showCountdownOverThumbnail
                                );
                            }}
                            label={t("video-upload:show-countdown")}
                            reverseLayout
                        />
                    </div>
                )}
            </div>
            {enableRecording && (
                <>
                    {showCategories && (
                        <div className={`${styles["categories-container"]}`}>
                            <CategoriesAssignment
                                categoryType={CategoryType._0}
                                selectedCategories={selectedCategories}
                                setSelectedCategories={setSelectedCategories}
                            />
                        </div>
                    )}
                    {exists(players) && !disablePlayerSelect && (
                        <DisplaySelectedButton
                            selected={players}
                            onClick={() => setPlaylistSelectModalIsOpen(true)}
                            label={
                                hasCatalogClaim
                                    ? t("video-upload:collections")
                                    : t("video-upload:players")
                            }
                            buttonText={
                                hasCatalogClaim
                                    ? t("video-upload:add-to-collections")
                                    : t("video-upload:add-to-playlists")
                            }
                            //Singular key here for dynamic pluralization of text
                            itemText={
                                hasCatalogClaim
                                    ? t("video-upload:collection")
                                    : t("video-upload:player")
                            }
                            variant="block"
                        />
                    )}
                    {exists(links) && (
                        <DisplaySelectedButton
                            selected={links}
                            onClick={() => setLinkSelectModalIsOpen(true)}
                            label={t("video-upload:links")}
                            buttonText={t("video-upload:add-links")}
                            //Singular key here for dynamic pluralization of text
                            itemText={t("video-upload:link")}
                            variant="block"
                        />
                    )}
                </>
            )}
        </div>
    );
};
